import type { SpreadDetail, SpreadPlacedCardView, SpreadSessionView } from '@creativitas/shared';

/**
 * Что лежит в этой позиции у того, чей расклад мы смотрим.
 *
 * У каждого участника свой расклад: одна и та же позиция заполняется каждым
 * отдельно. Без второй половины условия в позиции оказывалась бы чужая карта, а
 * своя рубашка выглядела бы занятой.
 */
export function placedInPosition(
  session: SpreadSessionView,
  positionId: string,
  viewedParticipantId?: string,
): SpreadPlacedCardView | undefined {
  return session.placedCards.find(
    (card) =>
      card.positionId === positionId &&
      (viewedParticipantId === undefined || card.placedByParticipantId === viewedParticipantId),
  );
}

/** Чья это карта — в тех словах, которые нужны читателю (SPR-15). */
export function ownerLabel(
  session: SpreadSessionView,
  ownerId: string | null,
  viewerParticipantId?: string,
): string {
  if (!ownerId) return 'Карта стола';
  if (ownerId === viewerParticipantId) return 'Ваша карта';
  return participantName(session, ownerId) ?? 'Карта участника';
}

/**
 * Имя участника — и того, кого ведущий уже удалил: его карты остаются в
 * истории, и подпись под ними не должна превращаться в «карту участника».
 */
export function participantName(session: SpreadSessionView, participantId: string): string | undefined {
  const present = session.participants.find((participant) => participant.id === participantId);
  if (present) return present.displayName;
  const removed = session.removedParticipants.find((participant) => participant.id === participantId);
  return removed ? `${removed.displayName} (удалён)` : undefined;
}

/** Центр клетки в процентах от стороны доски. Доска обрезана ровно по сетке. */
export const cellCentre = (index: number, grid: number): number => ((index + 0.5) * 100) / grid;

/**
 * Доска этого расклада — схема позиций, а не место, куда кладут карты.
 *
 * У «Шахмат» поле не стол: клетка названа фигурой, которая на ней стоит, и
 * закрывать фигуру картой значит стирать сам расклад. Поэтому на доске стоят
 * фигуры, а карта открывается по нажатию и живёт в панели под доской.
 *
 * Признак берётся из данных, а не из slug: разметка позиции несёт фигуру
 * (`marker`), и её наличие у каждой позиции и есть заявление «эту позицию
 * представляет фигура». Расклад без фигур на доске — даже если доска у него
 * есть — по-прежнему раскладывается картами, а «Своя игра» сюда не попадает
 * вовсе: она свободный стол, и позиций у неё нет.
 */
export function isFigureBoard(spread: SpreadDetail): boolean {
  return (
    spread.layout === 'preset' &&
    Boolean(spread.boardImage) &&
    spread.positions.length > 0 &&
    spread.positions.every((position) => position.marker !== null)
  );
}
